import { hasPermission as hasStoredPermission } from "./userPermissions";

/** General finance module (fee rules, GST, branding, health insurance, etc.). */
export function canAccessFinanceModule() {
  return hasStoredPermission("finance");
}

export function canViewProfessionalCost() {
  return hasStoredPermission("professionalCost");
}

export function canViewApplicationCommissionReport() {
  return hasStoredPermission("show_application_commission");
}

export function canViewCommissionInstallmentsQueue() {
  return (
    canViewCommissionInvoices() ||
    canViewCommissionModule() ||
    canAccessFinanceModule()
  );
}

/** Any Manage Finance sidebar / finance area access. */
export function canAccessAnyFinanceArea() {
  return (
    canAccessFinanceModule() ||
    canViewProfessionalCost() ||
    canAccessFinanceInvoicesHub() ||
    canViewCommissionModule() ||
    canViewApplicationCommissionReport()
  );
}

/** Awaiting Approval tab on invoice list pages */
export function canAccessAwaitingApproval() {
  return hasStoredPermission("awaiting_approval");
}

/** Delete invoice button on invoice list pages */
export function canDeleteFinanceInvoice() {
  return hasStoredPermission("delete_invoice");
}

/** Move a student from Commissionable Students back to General Report. */
export function canRemoveFromCommissionableStudents() {
  return hasStoredPermission("remove_from_commissionable");
}

export function canBulkCommissionMarkCompleted() {
  return hasStoredPermission("bulk_commission_mark_completed");
}

export function canBulkCommissionDiscontinue() {
  return hasStoredPermission("bulk_commission_discontinue");
}

export function canBulkCommissionReactivate() {
  return hasStoredPermission("bulk_commission_reactivate");
}

export function canBulkCommissionRevertCompleted() {
  return hasStoredPermission("bulk_commission_revert_completed");
}

export function canBulkCommissionSubmitFinalReview() {
  return hasStoredPermission("bulk_commission_submit_final_review");
}

export function canBulkCommissionApproveFinalReview() {
  return hasStoredPermission("bulk_commission_approve_final_review");
}

export function canBulkCommissionReturnFinalReview() {
  return hasStoredPermission("bulk_commission_return_final_review");
}

export function canBulkCommissionCreateInquiryBatch() {
  return hasStoredPermission("bulk_commission_create_inquiry_batch");
}

/**
 * Bulk actions require explicit Finance permissions (default off).
 * Enable each bulk flag on Permissions / permission groups.
 *
 * @param {string} actionId
 */
export function canBulkCommissionAction(actionId) {
  if (actionId === "remove_from_commissionable") {
    return canRemoveFromCommissionableStudents();
  }

  const map = {
    mark_completed: canBulkCommissionMarkCompleted,
    discontinue: canBulkCommissionDiscontinue,
    reactivate: canBulkCommissionReactivate,
    revert_completed: canBulkCommissionRevertCompleted,
    submit_final_review: canBulkCommissionSubmitFinalReview,
    approve_final_review: canBulkCommissionApproveFinalReview,
    return_final_review: canBulkCommissionReturnFinalReview,
  };

  const fn = map[actionId];
  return fn ? fn() : false;
}

/**
 * Edit invoice content (line items / branding) from the chevron.
 * Draft: everyone. After draft: requires edit_invoice.
 */
export function canEditFinanceInvoiceByStatus(status) {
  const normalized = String(status || "")
    .toLowerCase()
    .trim()
    .replace(/-/g, "_");
  if (normalized === "draft") {
    return canAccessFinanceInvoicesHub();
  }
  return hasStoredPermission("edit_invoice");
}

export function canEditFinanceInvoice() {
  return hasStoredPermission("edit_invoice");
}

/** Finance → Invoices → Application invoices tab (explicit permission only). */
export function canViewApplicationInvoices() {
  return hasStoredPermission("view_application_invoices");
}

/** Finance → Invoices → Commission invoices tab (explicit permission only). */
export function canViewCommissionInvoices() {
  return hasStoredPermission("view_commission_invoices");
}

/** Sidebar “Commissions” and /commissions-invoices workspace (explicit permission only). */
export function canViewCommissionModule() {
  return hasStoredPermission("view_commission_module");
}

/**
 * “Commissionable Students” on /commissions-invoices (separate from commission module access).
 * Finance → Commission invoices tab uses {@link canViewCommissionInvoices} directly.
 */
export function canViewCommissionableStudentsTab() {
  return canViewCommissionInvoices();
}

/** Create/edit commission invoices (commissions workspace or finance commission tab). */
export function canAccessCommissionInvoiceEditor() {
  return canViewCommissionInvoices();
}

/** Finance → Invoices hub: at least one invoice list tab. */
export function canAccessFinanceInvoicesHub() {
  return canViewApplicationInvoices() || canViewCommissionInvoices();
}

/**
 * Pick a module tab the user is allowed to see on /finance/invoices.
 * @param {"application"|"commission"|string} moduleTab
 */
export function resolveFinanceInvoicesModuleTab(
  moduleTab,
  {
    canApplication = canViewApplicationInvoices(),
    canCommission = canViewCommissionInvoices(),
  } = {},
) {
  const requested =
    moduleTab === "commission" || moduleTab === "application"
      ? moduleTab
      : "application";

  if (requested === "application" && canApplication) return "application";
  if (requested === "commission" && canCommission) return "commission";
  if (canApplication) return "application";
  if (canCommission) return "commission";
  return requested;
}
